import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, UsersIcon, DollarSignIcon, StarIcon } from 'lucide-react';
interface SeatingOption {
  id: number;
  name: string;
  capacity: string;
  startingPrice: string;
  gradient: string;
  accentGradient: string;
  description: string;
  amenities: string[];
  tag: string;
}
export function SeatingOptions() {
  const [selectedSeat, setSelectedSeat] = useState<SeatingOption | null>(null);
  return <section id="seating" className="w-full bg-[#050505] py-20 px-4 sm:px-6" data-id="element-336">
      <div className="max-w-7xl mx-auto" data-id="element-337">
        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="text-center mb-12" data-id="element-338">
          <span className="font-inter text-[11px] tracking-[0.4em] text-[#C9A84C]/60 uppercase" data-id="element-339">
            Seating & Reservations
          </span>
          <h2 className="font-montserrat font-black text-3xl sm:text-4xl lg:text-5xl tracking-widest text-white mt-2" data-id="element-340">
            CHOOSE YOUR VIBE
          </h2>
          <div className="w-14 h-px bg-[#C9A84C]/50 mx-auto mt-4" data-id="element-341" />
        </motion.div>

        {/* Horizontal cards — scroll on mobile, stack on desktop */}
        <div className="flex flex-col gap-4 sm:gap-5" data-id="element-342">
          {/* Card 1: Main Floor */}
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.55,
          delay: 0
        }} onClick={() => setSelectedSeat({
          id: 1,
          name: 'MAIN FLOOR',
          capacity: '2–8 guests',
          startingPrice: '$500 min spend',
          gradient: 'linear-gradient(160deg, #0a0500 0%, #1a0800 40%, #2a1200 70%, #C9A84C20 100%)',
          accentGradient: 'linear-gradient(90deg, #0a0500, #1a0800)',
          description: 'Immerse yourself in the heart of the action. Prime tables with direct views of the main stage and dance floor.',
          amenities: ['Dedicated bottle service', 'Priority entry', 'Personal host', 'Stage view'],
          tag: 'MOST POPULAR'
        })} className="relative rounded-2xl overflow-hidden cursor-pointer group" style={{
          border: '1px solid #2A2622',
          boxShadow: '0 8px 40px rgba(0,0,0,0.5)',
          height: 140
        }} data-id="element-343">
            {/* Background */}
            <div className="absolute inset-0 transition-transform duration-700 group-hover:scale-105" style={{
            background: 'linear-gradient(160deg, #0a0500 0%, #1a0800 40%, #2a1200 70%, #C9A84C20 100%)'
          }} data-id="element-344" />
            <div className="absolute inset-0 opacity-20" style={{
            background: 'radial-gradient(ellipse at 80% 50%, rgba(201,168,76,0.4) 0%, transparent 60%)'
          }} data-id="element-345" />
            <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#050505]/60" data-id="element-346" />

            {/* Hover gold shimmer */}
            <div className="absolute inset-0 bg-gradient-to-r from-[#C9A84C]/0 via-[#C9A84C]/5 to-[#C9A84C]/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" data-id="element-347" />

            {/* Content */}
            <div className="relative z-10 flex items-center h-full px-6 gap-6" data-id="element-348">
              {/* Left: name + info */}
              <div className="flex-1" data-id="element-349">
                <div className="flex items-center gap-3 mb-1" data-id="element-350">
                  <h3 className="font-montserrat font-black text-xl tracking-widest text-white" data-id="element-351">
                    MAIN FLOOR
                  </h3>
                  <span className="font-montserrat text-[8px] tracking-widest text-[#050505] bg-[#C9A84C] px-2 py-0.5 rounded" data-id="element-352">
                    POPULAR
                  </span>
                </div>
                <div className="flex items-center gap-4" data-id="element-353">
                  <div className="flex items-center gap-1.5 text-white/50" data-id="element-354">
                    <UsersIcon size={11} data-id="element-355" />
                    <span className="font-inter text-xs" data-id="element-356">2–8 guests</span>
                  </div>
                  <span className="font-montserrat font-700 text-xs text-[#C9A84C]" data-id="element-357">
                    from $500
                  </span>
                </div>
                <p className="font-inter font-light text-xs text-white/40 mt-1.5 max-w-xs hidden sm:block" data-id="element-358">
                  Prime tables with direct views of the main stage and dance
                  floor.
                </p>
              </div>

              {/* Right: CTA */}
              <button className="flex-shrink-0 font-montserrat font-black text-[10px] tracking-widest bg-[#C9A84C] text-[#050505] px-5 py-3 rounded-xl hover:bg-[#E8C97A] transition-colors duration-300" style={{
              boxShadow: '0 4px 16px rgba(201,168,76,0.3)'
            }} data-id="element-359">
                RESERVE
              </button>
            </div>
          </motion.div>

          {/* Card 2: VIP Section */}
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.55,
          delay: 0.1
        }} onClick={() => setSelectedSeat({
          id: 2,
          name: 'VIP SECTION',
          capacity: '4–12 guests',
          startingPrice: '$1,200 min spend',
          gradient: 'linear-gradient(160deg, #050010 0%, #0a0020 40%, #180040 70%, #C9A84C25 100%)',
          accentGradient: 'linear-gradient(90deg, #050010, #0a0020)',
          description: 'Elevated experience with semi-private booths, premium bottle service, and exclusive access to the VIP bar.',
          amenities: ['Semi-private booth', 'Premium bottle menu', 'VIP bar access', 'Dedicated security', 'Complimentary mixers'],
          tag: 'VIP'
        })} className="relative rounded-2xl overflow-hidden cursor-pointer group" style={{
          border: '1px solid #2A2622',
          boxShadow: '0 8px 40px rgba(0,0,0,0.5)',
          height: 140
        }} data-id="element-360">
            <div className="absolute inset-0 transition-transform duration-700 group-hover:scale-105" style={{
            background: 'linear-gradient(160deg, #050010 0%, #0a0020 40%, #180040 70%, #C9A84C25 100%)'
          }} data-id="element-361" />
            <div className="absolute inset-0 opacity-20" style={{
            background: 'radial-gradient(ellipse at 20% 50%, rgba(201,168,76,0.35) 0%, transparent 60%)'
          }} data-id="element-362" />
            <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#050505]/60" data-id="element-363" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#C9A84C]/0 via-[#C9A84C]/5 to-[#C9A84C]/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" data-id="element-364" />

            <div className="relative z-10 flex items-center h-full px-6 gap-6" data-id="element-365">
              <div className="flex-1" data-id="element-366">
                <div className="flex items-center gap-3 mb-1" data-id="element-367">
                  <h3 className="font-montserrat font-black text-xl tracking-widest text-white" data-id="element-368">
                    VIP SECTION
                  </h3>
                  <span className="font-montserrat text-[8px] tracking-widest text-[#C9A84C] border border-[#C9A84C]/50 px-2 py-0.5 rounded" data-id="element-369">
                    EXCLUSIVE
                  </span>
                </div>
                <div className="flex items-center gap-4" data-id="element-370">
                  <div className="flex items-center gap-1.5 text-white/50" data-id="element-371">
                    <UsersIcon size={11} data-id="element-372" />
                    <span className="font-inter text-xs" data-id="element-373">4–12 guests</span>
                  </div>
                  <span className="font-montserrat font-700 text-xs text-[#C9A84C]" data-id="element-374">
                    from $1,200
                  </span>
                </div>
                <p className="font-inter font-light text-xs text-white/40 mt-1.5 max-w-xs hidden sm:block" data-id="element-375">
                  Semi-private booths with premium bottle service and VIP bar
                  access.
                </p>
              </div>
              <button className="flex-shrink-0 font-montserrat font-black text-[10px] tracking-widest bg-[#C9A84C] text-[#050505] px-5 py-3 rounded-xl hover:bg-[#E8C97A] transition-colors duration-300" style={{
              boxShadow: '0 4px 16px rgba(201,168,76,0.3)'
            }} data-id="element-376">
                RESERVE
              </button>
            </div>
          </motion.div>

          {/* Card 3: Skybox Suite */}
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.55,
          delay: 0.2
        }} onClick={() => setSelectedSeat({
          id: 3,
          name: 'SKYBOX SUITE',
          capacity: '8–20 guests',
          startingPrice: '$3,000 min spend',
          gradient: 'linear-gradient(160deg, #0a0500 0%, #1a0a00 40%, #2a1500 70%, #E8C97A30 100%)',
          accentGradient: 'linear-gradient(90deg, #0a0500, #1a0a00)',
          description: 'The pinnacle of luxury. Panoramic views of the entire venue, private butler service, and an exclusive atmosphere.',
          amenities: ['Private suite', 'Butler service', 'Panoramic views', 'Custom menu', 'Private entrance', 'Concierge'],
          tag: 'PREMIUM'
        })} className="relative rounded-2xl overflow-hidden cursor-pointer group" style={{
          border: '1px solid #2A2622',
          boxShadow: '0 8px 40px rgba(0,0,0,0.5)',
          height: 140
        }} data-id="element-377">
            <div className="absolute inset-0 transition-transform duration-700 group-hover:scale-105" style={{
            background: 'linear-gradient(160deg, #0a0500 0%, #1a0a00 40%, #2a1500 70%, #E8C97A30 100%)'
          }} data-id="element-378" />
            <div className="absolute inset-0 opacity-25" style={{
            background: 'radial-gradient(ellipse at 70% 30%, rgba(232,201,122,0.4) 0%, transparent 55%)'
          }} data-id="element-379" />
            <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#050505]/60" data-id="element-380" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#C9A84C]/0 via-[#C9A84C]/5 to-[#C9A84C]/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" data-id="element-381" />

            <div className="relative z-10 flex items-center h-full px-6 gap-6" data-id="element-382">
              <div className="flex-1" data-id="element-383">
                <div className="flex items-center gap-3 mb-1" data-id="element-384">
                  <h3 className="font-montserrat font-black text-xl tracking-widest text-white" data-id="element-385">
                    SKYBOX SUITE
                  </h3>
                  <span className="font-montserrat text-[8px] tracking-widest text-[#050505] bg-[#E8C97A] px-2 py-0.5 rounded" data-id="element-386">
                    PREMIUM
                  </span>
                </div>
                <div className="flex items-center gap-4" data-id="element-387">
                  <div className="flex items-center gap-1.5 text-white/50" data-id="element-388">
                    <UsersIcon size={11} data-id="element-389" />
                    <span className="font-inter text-xs" data-id="element-390">8–20 guests</span>
                  </div>
                  <span className="font-montserrat font-700 text-xs text-[#E8C97A]" data-id="element-391">
                    from $3,000
                  </span>
                </div>
                <p className="font-inter font-light text-xs text-white/40 mt-1.5 max-w-xs hidden sm:block" data-id="element-392">
                  Panoramic views, private butler service, and the pinnacle of
                  luxury.
                </p>
              </div>
              <button className="flex-shrink-0 font-montserrat font-black text-[10px] tracking-widest bg-[#C9A84C] text-[#050505] px-5 py-3 rounded-xl hover:bg-[#E8C97A] transition-colors duration-300" style={{
              boxShadow: '0 4px 16px rgba(201,168,76,0.3)'
            }} data-id="element-393">
                RESERVE
              </button>
            </div>
          </motion.div>

          {/* Card 4: Bar Lounge */}
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.55,
          delay: 0.3
        }} onClick={() => setSelectedSeat({
          id: 4,
          name: 'BAR LOUNGE',
          capacity: '2–6 guests',
          startingPrice: '$300 min spend',
          gradient: 'linear-gradient(160deg, #050505 0%, #0f0f00 40%, #1a1a00 70%, #C9A84C18 100%)',
          accentGradient: 'linear-gradient(90deg, #050505, #0f0f00)',
          description: 'Sophisticated bar seating with craft cocktails and an intimate atmosphere perfect for smaller groups.',
          amenities: ['Premium bar access', 'Craft cocktail menu', 'Lounge seating', 'Happy hour specials'],
          tag: 'INTIMATE'
        })} className="relative rounded-2xl overflow-hidden cursor-pointer group" style={{
          border: '1px solid #2A2622',
          boxShadow: '0 8px 40px rgba(0,0,0,0.5)',
          height: 140
        }} data-id="element-394">
            <div className="absolute inset-0 transition-transform duration-700 group-hover:scale-105" style={{
            background: 'linear-gradient(160deg, #050505 0%, #0f0f00 40%, #1a1a00 70%, #C9A84C18 100%)'
          }} data-id="element-395" />
            <div className="absolute inset-0 opacity-20" style={{
            background: 'radial-gradient(ellipse at 40% 60%, rgba(201,168,76,0.3) 0%, transparent 60%)'
          }} data-id="element-396" />
            <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#050505]/60" data-id="element-397" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#C9A84C]/0 via-[#C9A84C]/5 to-[#C9A84C]/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" data-id="element-398" />

            <div className="relative z-10 flex items-center h-full px-6 gap-6" data-id="element-399">
              <div className="flex-1" data-id="element-400">
                <div className="flex items-center gap-3 mb-1" data-id="element-401">
                  <h3 className="font-montserrat font-black text-xl tracking-widest text-white" data-id="element-402">
                    BAR LOUNGE
                  </h3>
                  <span className="font-montserrat text-[8px] tracking-widest text-[#C9A84C] border border-[#C9A84C]/40 px-2 py-0.5 rounded" data-id="element-403">
                    INTIMATE
                  </span>
                </div>
                <div className="flex items-center gap-4" data-id="element-404">
                  <div className="flex items-center gap-1.5 text-white/50" data-id="element-405">
                    <UsersIcon size={11} data-id="element-406" />
                    <span className="font-inter text-xs" data-id="element-407">2–6 guests</span>
                  </div>
                  <span className="font-montserrat font-700 text-xs text-[#C9A84C]" data-id="element-408">
                    from $300
                  </span>
                </div>
                <p className="font-inter font-light text-xs text-white/40 mt-1.5 max-w-xs hidden sm:block" data-id="element-409">
                  Craft cocktails and intimate atmosphere for smaller,
                  sophisticated groups.
                </p>
              </div>
              <button className="flex-shrink-0 font-montserrat font-black text-[10px] tracking-widest bg-[#C9A84C] text-[#050505] px-5 py-3 rounded-xl hover:bg-[#E8C97A] transition-colors duration-300" style={{
              boxShadow: '0 4px 16px rgba(201,168,76,0.3)'
            }} data-id="element-410">
                RESERVE
              </button>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Spring Lightbox */}
      <AnimatePresence data-id="element-411">
        {selectedSeat && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.3
        }} className="fixed inset-0 z-50 bg-black/85" style={{
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)'
        }} onClick={() => setSelectedSeat(null)} data-id="element-412" />

            <motion.div initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.85
        }} transition={{
          type: 'spring',
          stiffness: 300,
          damping: 30
        }} className="fixed inset-4 sm:inset-8 lg:inset-auto lg:left-1/2 lg:top-1/2 lg:-translate-x-1/2 lg:-translate-y-1/2 z-50 rounded-2xl overflow-hidden flex flex-col" style={{
          background: '#0d0b09',
          border: '1px solid #2A2622',
          boxShadow: '0 24px 80px rgba(0,0,0,0.8), 0 0 0 1px rgba(201,168,76,0.1)',
          maxWidth: 560,
          maxHeight: '90vh'
        }} data-id="element-413">
              {/* Image header */}
              <div className="relative flex-shrink-0" style={{
            height: 200,
            background: selectedSeat.gradient
          }} data-id="element-414">
                <div className="absolute inset-0 opacity-25" style={{
              background: 'radial-gradient(ellipse at 60% 40%, rgba(201,168,76,0.4) 0%, transparent 60%)'
            }} data-id="element-415" />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent to-[#0d0b09]" data-id="element-416" />

                <button onClick={() => setSelectedSeat(null)} className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-full border border-[#2A2622] text-white/60 hover:border-[#C9A84C]/60 hover:text-[#C9A84C] transition-all z-10" style={{
              background: 'rgba(5,5,5,0.7)'
            }} aria-label="Close" data-id="element-417">
                  <XIcon size={15} data-id="element-418" />
                </button>

                <div className="absolute bottom-4 left-6" data-id="element-419">
                  <h2 className="font-montserrat font-black text-2xl tracking-widest text-white" data-id="element-420">
                    {selectedSeat.name}
                  </h2>
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6" data-id="element-421">
                <p className="font-inter font-light text-white/60 text-sm leading-relaxed mb-5" data-id="element-422">
                  {selectedSeat.description}
                </p>

                <div className="grid grid-cols-2 gap-3 mb-5" data-id="element-423">
                  <div className="rounded-xl p-4" style={{
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid #2A2622'
              }} data-id="element-424">
                    <div className="flex items-center gap-2 mb-1" data-id="element-425">
                      <UsersIcon size={12} className="text-[#C9A84C]" data-id="element-426" />
                      <span className="font-montserrat text-[9px] tracking-widest text-white/40" data-id="element-427">
                        CAPACITY
                      </span>
                    </div>
                    <p className="font-montserrat font-700 text-sm text-white" data-id="element-428">
                      {selectedSeat.capacity}
                    </p>
                  </div>
                  <div className="rounded-xl p-4" style={{
                background: 'rgba(201,168,76,0.08)',
                border: '1px solid rgba(201,168,76,0.2)'
              }} data-id="element-429">
                    <div className="flex items-center gap-2 mb-1" data-id="element-430">
                      <DollarSignIcon size={12} className="text-[#C9A84C]" data-id="element-431" />
                      <span className="font-montserrat text-[9px] tracking-widest text-white/40" data-id="element-432">
                        STARTING AT
                      </span>
                    </div>
                    <p className="font-montserrat font-black text-sm text-[#C9A84C]" data-id="element-433">
                      {selectedSeat.startingPrice}
                    </p>
                  </div>
                </div>

                <div className="mb-6" data-id="element-434">
                  <p className="font-montserrat text-[9px] tracking-widest text-white/30 mb-3" data-id="element-435">
                    AMENITIES
                  </p>
                  <div className="flex flex-wrap gap-2" data-id="element-436">
                    {selectedSeat.amenities.map(a => <div key={a} className="flex items-center gap-1.5 rounded-full px-3 py-1" style={{
                  background: 'rgba(201,168,76,0.08)',
                  border: '1px solid rgba(201,168,76,0.2)'
                }} data-id="element-437">
                        <StarIcon size={8} className="text-[#C9A84C]" data-id="element-438" />
                        <span className="font-inter text-xs text-white/70" data-id="element-439">
                          {a}
                        </span>
                      </div>)}
                  </div>
                </div>

                <button onClick={() => setSelectedSeat(null)} className="w-full font-montserrat font-black text-xs tracking-widest bg-[#C9A84C] text-[#050505] py-4 rounded-xl hover:bg-[#E8C97A] transition-colors duration-300" style={{
              boxShadow: '0 4px 20px rgba(201,168,76,0.3)'
            }} data-id="element-440">
                  RESERVE THIS SPACE
                </button>

                <p className="text-center font-inter text-[10px] text-white/25 mt-3" data-id="element-441">
                  Swipe to browse on mobile
                </p>
              </div>
            </motion.div>
          </>}
      </AnimatePresence>
    </section>;
}